import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { C, USERS } from '../styles/tokens.js';
import CardEditPopup from './CardEditPopup.jsx';
import FilePickerModal from './FilePickerModal.jsx';
import PdfThumbnail from './PdfThumbnail.jsx';

// 가상 캔버스 크기 (5배 확장)
const WORLD_W = 5000;
const WORLD_H = 3500;
const MINIMAP_W = 200;
const MINIMAP_H = 140;

const TODAY = new Date('2026-06-29');
const EMOJI_LIST = ['👍', '✅', '❗', '🔥', '💡', '❤️', '🎯', '🤔'];
const PALETTE = [C.gold, C.blue, C.green, C.red, C.purple, C.orange];

function fmtSize(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b/1024).toFixed(1)} KB`;
  return `${(b/1048576).toFixed(1)} MB`;
}

function useHistory() {
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const push = useCallback((snap) => {
    setPast(p => [...p.slice(-49), snap]);
    setFuture([]);
  }, []);
  const popUndo = useCallback((current) => {
    if (past.length === 0) return null;
    const last = past[past.length - 1];
    setPast(p => p.slice(0, -1));
    setFuture(f => [...f, current]);
    return last;
  }, [past]);
  const popRedo = useCallback((current) => {
    if (future.length === 0) return null;
    const next = future[future.length - 1];
    setFuture(f => f.slice(0, -1));
    setPast(p => [...p, current]);
    return next;
  }, [future]);
  return { past, future, push, popUndo, popRedo, canUndo: past.length > 0, canRedo: future.length > 0 };
}

export default function WhiteboardTab({ addLog, data, setData }) {
  const [mode, setMode] = useState('select');
  const cards = data.cards || [];
  const areas = data.areas || [];
  const dates = data.dates || [];
  const paths = data.paths || [];

  const setCards = (fn) => setData(d => ({ ...d, cards: typeof fn === 'function' ? fn(d.cards || []) : fn }));
  const setAreas = (fn) => setData(d => ({ ...d, areas: typeof fn === 'function' ? fn(d.areas || []) : fn }));
  const setDates = (fn) => setData(d => ({ ...d, dates: typeof fn === 'function' ? fn(d.dates || []) : fn }));
  const setPaths = (fn) => setData(d => ({ ...d, paths: typeof fn === 'function' ? fn(d.paths || []) : fn }));

  const [zoom, setZoom] = useState(0.8);

  // 뷰포트 패닝 (가상 캔버스 위치)
  const viewportRef = useRef(null);
  const [viewport, setViewport] = useState({ w: 1200, h: 700 });
  const [panning, setPanning] = useState(false);
  const panStartRef = useRef(null);
  const [spaceDown, setSpaceDown] = useState(false);

  // 드래그
  const [dragCard, setDragCard] = useState(null);
  const [dragArea, setDragArea] = useState(null);
  const [dragDate, setDragDate] = useState(null);
  const [resizeArea, setResizeArea] = useState(null);
  const [resizeDate, setResizeDate] = useState(null);
  const [dragOff, setDragOff] = useState({ x: 0, y: 0 });
  const [hoverArea, setHoverArea] = useState(null);

  // 펜
  const canvasRef = useRef(null);
  const [penColor, setPenColor] = useState(C.gold);
  const [penWidth, setPenWidth] = useState(3);
  const drawingRef = useRef(false);
  const currentPathRef = useRef(null);

  const [emojiCard, setEmojiCard] = useState(null);
  const [commentCard, setCommentCard] = useState(null);
  const [areaEdit, setAreaEdit] = useState(null);
  const [dateEdit, setDateEdit] = useState(null);
  const [cardEdit, setCardEdit] = useState(null);
  const [filePickerCard, setFilePickerCard] = useState(null);
  const [resizeCard, setResizeCard] = useState(null);

  const hist = useHistory();
  const snapshot = useCallback(() => ({
    cards: JSON.parse(JSON.stringify(cards)),
    areas: JSON.parse(JSON.stringify(areas)),
    dates: JSON.parse(JSON.stringify(dates)),
    paths: JSON.parse(JSON.stringify(paths)),
  }), [cards, areas, dates, paths]);
  const beforeChange = useCallback(() => hist.push(snapshot()), [hist, snapshot]);

  const undo = useCallback(() => {
    const cur = snapshot();
    const last = hist.popUndo(cur);
    if (!last) return;
    setData(d => ({ ...d, ...last }));
    addLog('윤대표', 'whiteboard', '↶ 되돌리기');
  }, [hist, snapshot, setData, addLog]);

  const redo = useCallback(() => {
    const cur = snapshot();
    const next = hist.popRedo(cur);
    if (!next) return;
    setData(d => ({ ...d, ...next }));
    addLog('윤대표', 'whiteboard', '↷ 다시 실행');
  }, [hist, snapshot, setData, addLog]);

  // viewport 크기 측정
  useEffect(() => {
    const measure = () => {
      if (!viewportRef.current) return;
      const r = viewportRef.current.getBoundingClientRect();
      setViewport({ w: r.width, h: r.height });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // ── Ctrl+Z / Ctrl+Y / Ctrl+Shift+Z + Space 패닝 ──
  useEffect(() => {
    const onKey = (e) => {
      // 스페이스 = 패닝 모드
      if (e.code === 'Space' && !spaceDown) {
        const tag = (e.target.tagName || '').toLowerCase();
        if (!['input', 'textarea'].includes(tag)) {
          e.preventDefault();
          setSpaceDown(true);
        }
        return;
      }
      const meta = e.ctrlKey || e.metaKey;
      if (!meta) return;
      const tag = (e.target.tagName || '').toLowerCase();
      const editable = e.target.isContentEditable;
      if (['input', 'textarea'].includes(tag) || editable) return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); e.stopPropagation(); undo(); }
      else if (k === 'y' || (k === 'z' && e.shiftKey)) { e.preventDefault(); e.stopPropagation(); redo(); }
    };
    const onKeyUp = (e) => {
      if (e.code === 'Space') setSpaceDown(false);
    };
    // window + document 양쪽 등록 (이벤트 가로채임 방지)
    window.addEventListener('keydown', onKey, true); // capture
    window.addEventListener('keyup', onKeyUp);
    document.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKeyUp);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [undo, redo, spaceDown]);

  // ── 드래그 시작 ──
  const onCardDown = (e, id) => {
    if (mode !== 'select' || spaceDown) return;
    // preventDefault 제거! → button click이 발동되도록
    // userSelect: 'none'으로 텍스트 선택 차단은 별도 처리됨
    e.stopPropagation();
    beforeChange();
    const c = cards.find(x => x.id === id);
    setDragCard(id);
    setDragOff({ x: e.clientX - c.x * zoom, y: e.clientY - c.y * zoom });
  };
  const onAreaDown = (e, id) => {
    if (mode !== 'select' || spaceDown) return;
    e.stopPropagation();
    beforeChange();
    const a = areas.find(x => x.id === id);
    setDragArea(id);
    setDragOff({ x: e.clientX - a.x * zoom, y: e.clientY - a.y * zoom });
  };
  const onAreaResizeDown = (e, id) => {
    e.preventDefault(); e.stopPropagation();
    beforeChange();
    const a = areas.find(x => x.id === id);
    setResizeArea({ id, startX: e.clientX, startY: e.clientY, startW: a.w, startH: a.h });
  };
  const onDateDown = (e, id) => {
    if (mode !== 'select' || spaceDown) return;
    e.stopPropagation();
    beforeChange();
    const d = dates.find(x => x.id === id);
    setDragDate(id);
    setDragOff({ x: e.clientX - d.x * zoom, y: e.clientY - d.y * zoom });
  };
  const onDateResizeDown = (e, id) => {
    e.preventDefault(); e.stopPropagation();
    beforeChange();
    const d = dates.find(x => x.id === id);
    setResizeDate({ id, startX: e.clientX, startY: e.clientY, startW: d.w, startH: d.h });
  };

  // ── 캔버스 빈 영역 mousedown - 펜 또는 패닝 ──
  const onCanvasDown = (e) => {
    if (spaceDown || e.button === 1) {
      // 패닝 (Space 누름 or 휠 클릭)
      e.preventDefault();
      const v = viewportRef.current;
      panStartRef.current = { x: e.clientX, y: e.clientY, scrollLeft: v.scrollLeft, scrollTop: v.scrollTop };
      setPanning(true);
      return;
    }
    if (mode !== 'pen') return;
    beforeChange();
    const r = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / zoom;
    const y = (e.clientY - r.top) / zoom;
    drawingRef.current = true;
    currentPathRef.current = { id: Date.now(), color: penColor, width: penWidth, points: [{ x, y }] };
    setPaths(p => [...p, currentPathRef.current]);
  };

  // ── 마우스 이동 (드래그·리사이즈·펜·패닝) ──
  const onMove = useCallback((e) => {
    if (panning && panStartRef.current && viewportRef.current) {
      const v = viewportRef.current;
      v.scrollLeft = panStartRef.current.scrollLeft - (e.clientX - panStartRef.current.x);
      v.scrollTop = panStartRef.current.scrollTop - (e.clientY - panStartRef.current.y);
      return;
    }
    if (dragCard != null) {
      setCards(prev => prev.map(c => c.id === dragCard
        ? { ...c, x: Math.max(0, Math.min(WORLD_W - c.w, (e.clientX - dragOff.x) / zoom)), y: Math.max(0, Math.min(WORLD_H - c.h, (e.clientY - dragOff.y) / zoom)) }
        : c));
    } else if (dragArea != null) {
      setAreas(prev => prev.map(a => a.id === dragArea
        ? { ...a, x: Math.max(0, Math.min(WORLD_W - a.w, (e.clientX - dragOff.x) / zoom)), y: Math.max(0, Math.min(WORLD_H - a.h, (e.clientY - dragOff.y) / zoom)) }
        : a));
    } else if (dragDate != null) {
      setDates(prev => prev.map(d => d.id === dragDate
        ? { ...d, x: Math.max(0, Math.min(WORLD_W - d.w, (e.clientX - dragOff.x) / zoom)), y: Math.max(0, Math.min(WORLD_H - d.h, (e.clientY - dragOff.y) / zoom)) }
        : d));
    } else if (resizeArea != null) {
      const dx = (e.clientX - resizeArea.startX) / zoom;
      const dy = (e.clientY - resizeArea.startY) / zoom;
      setAreas(prev => prev.map(a => a.id === resizeArea.id ? { ...a, w: Math.max(120, resizeArea.startW + dx), h: Math.max(80, resizeArea.startH + dy) } : a));
    } else if (resizeDate != null) {
      const dx = (e.clientX - resizeDate.startX) / zoom;
      const dy = (e.clientY - resizeDate.startY) / zoom;
      setDates(prev => prev.map(d => d.id === resizeDate.id ? { ...d, w: Math.max(160, resizeDate.startW + dx), h: Math.max(90, resizeDate.startH + dy) } : d));
    } else if (drawingRef.current && mode === 'pen' && currentPathRef.current) {
      const r = canvasRef.current.getBoundingClientRect();
      const x = (e.clientX - r.left) / zoom;
      const y = (e.clientY - r.top) / zoom;
      if (isFinite(x) && isFinite(y)) {
        currentPathRef.current.points.push({ x, y });
        setPaths(prev => [...prev.slice(0, -1), { ...currentPathRef.current }]);
      }
    }
  }, [dragCard, dragArea, dragDate, resizeArea, resizeDate, dragOff, zoom, mode, panning]);

  const onUp = useCallback(() => {
    if (panning) { panStartRef.current = null; setPanning(false); }
    if (dragCard != null) { addLog('윤대표', 'whiteboard', `카드 이동`); setDragCard(null); }
    if (dragArea != null) { addLog('윤대표', 'whiteboard', `영역 이동`); setDragArea(null); }
    if (dragDate != null) { addLog('윤대표', 'whiteboard', `날짜 이동`); setDragDate(null); }
    if (resizeArea != null) { addLog('윤대표', 'whiteboard', `영역 리사이즈`); setResizeArea(null); }
    if (resizeDate != null) { addLog('윤대표', 'whiteboard', `날짜 리사이즈`); setResizeDate(null); }
    if (drawingRef.current) {
      drawingRef.current = false;
      const cur = currentPathRef.current;
      currentPathRef.current = null;
      if (cur && (!Array.isArray(cur.points) || cur.points.length < 2)) {
        setPaths(prev => prev.filter(p => p.id !== cur.id));
      } else {
        addLog('윤대표', 'whiteboard', '펜 스트로크 추가');
      }
    }
  }, [dragCard, dragArea, dragDate, resizeArea, resizeDate, panning, addLog]);

  useEffect(() => {
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [onMove, onUp]);

  // Canvas 렌더 (가상 캔버스 크기로)
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const dpr = window.devicePixelRatio || 1;
    cv.width = WORLD_W * zoom * dpr;
    cv.height = WORLD_H * zoom * dpr;
    const ctx = cv.getContext('2d');
    ctx.setTransform(dpr * zoom, 0, 0, dpr * zoom, 0, 0);
    ctx.clearRect(0, 0, WORLD_W, WORLD_H);
    try {
      paths.forEach(p => {
        if (!p || !Array.isArray(p.points) || p.points.length < 2) return;
        const valid = p.points.every(pt => pt && typeof pt.x === 'number' && typeof pt.y === 'number' && isFinite(pt.x) && isFinite(pt.y));
        if (!valid) return;
        ctx.strokeStyle = p.color || '#C9A84C';
        ctx.lineWidth = p.width || 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(p.points[0].x, p.points[0].y);
        p.points.slice(1).forEach(pt => ctx.lineTo(pt.x, pt.y));
        ctx.stroke();
      });
    } catch (e) { console.warn('Canvas render error:', e); }
  }, [paths, zoom]);

  // ── 추가/삭제 ──
  const addCard = (type) => {
    beforeChange();
    const v = viewportRef.current;
    const cx = v ? (v.scrollLeft + v.clientWidth / 2) / zoom : WORLD_W / 2;
    const cy = v ? (v.scrollTop + v.clientHeight / 2) / zoom : WORLD_H / 2;
    const colorMap = { note: C.gold, task: C.blue, file: C.green, media: C.orange };
    setCards(prev => [...prev, {
      id: Date.now(), type, x: cx - 100, y: cy - 55, w: 200, h: 110,
      title: type === 'file' ? '새 파일' : (type === 'media' ? '새 미디어' : '새 항목'),
      content: (type === 'file' || type === 'media') ? '↓ 클릭해 파일 첨부' : '내용을 입력하세요',
      color: colorMap[type], reactions: {}, comments: [], file: null,
    }]);
    addLog('윤대표', 'whiteboard', `카드 추가: ${type}`);
  };
  const addArea = () => {
    beforeChange();
    const v = viewportRef.current;
    const cx = v ? (v.scrollLeft + v.clientWidth / 2) / zoom : WORLD_W / 2;
    const cy = v ? (v.scrollTop + v.clientHeight / 2) / zoom : WORLD_H / 2;
    setAreas(prev => [...prev, { id: Date.now(), x: cx - 160, y: cy - 100, w: 320, h: 200, name: '새 영역', color: C.orange }]);
    addLog('윤대표', 'whiteboard', '영역 추가');
  };
  const addDate = () => {
    beforeChange();
    const v = viewportRef.current;
    const cx = v ? (v.scrollLeft + v.clientWidth / 2) / zoom : WORLD_W / 2;
    const cy = v ? (v.scrollTop + v.clientHeight / 2) / zoom : WORLD_H / 2;
    const t = new Date(TODAY); t.setDate(t.getDate() + 7);
    setDates(prev => [...prev, { id: Date.now(), x: cx - 110, y: cy - 65, w: 220, h: 130, label: '마감일', date: t.toISOString().slice(0, 10), color: C.red }]);
    addLog('윤대표', 'whiteboard', '마감일 추가');
  };
  const deleteCard = (id) => { beforeChange(); setCards(p => p.filter(x => x.id !== id)); addLog('윤대표', 'whiteboard', '카드 삭제'); };
  const deleteArea = (id) => { beforeChange(); setAreas(p => p.filter(a => a.id !== id)); setAreaEdit(null); addLog('윤대표', 'whiteboard', '영역 삭제'); };
  const deleteDate = (id) => { beforeChange(); setDates(p => p.filter(d => d.id !== id)); setDateEdit(null); addLog('윤대표', 'whiteboard', '마감일 삭제'); };

  const addReaction = (cardId, emoji) => {
    beforeChange();
    setCards(prev => prev.map(c => c.id === cardId ? { ...c, reactions: { ...c.reactions, [emoji]: (c.reactions[emoji] || 0) + 1 } } : c));
    addLog('윤대표', 'whiteboard', `이모지 ${emoji}`);
    setEmojiCard(null);
  };
  const addComment = (cardId, text) => {
    if (!text.trim()) return;
    beforeChange();
    setCards(prev => prev.map(c => c.id === cardId ? { ...c, comments: [...c.comments, { id: Date.now(), by: '윤대표', text }] } : c));
    addLog('윤대표', 'comment', `댓글 추가`);
  };
  const onFilePick = (cardId, file) => {
    if (!file) return;
    beforeChange();
    setCards(prev => prev.map(c => c.id === cardId ? { ...c, title: file.name, content: `${fmtSize(file.size)} · ${new Date().toISOString().slice(0,10)}`, file: { name: file.name, size: file.size, type: file.type } } : c));
    addLog('윤대표', 'file', `파일 첨부: ${file.name}`);
  };

  const TYPE_LABEL = { note: '메모', task: '수정', file: '파일', media: '미디어' };
  const [mediaPlaying, setMediaPlaying] = useState({});
  const calcDDay = (iso) => Math.ceil((new Date(iso) - TODAY) / 86400000);

  // 줌 wheel
  const onWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = -e.deltaY * 0.001;
      setZoom(z => Math.max(0.2, Math.min(2, z + delta)));
    }
  };

  // 미니맵 클릭 → viewport 이동
  const onMinimapClick = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const rx = (e.clientX - r.left) / MINIMAP_W;
    const ry = (e.clientY - r.top) / MINIMAP_H;
    const v = viewportRef.current;
    if (v) {
      v.scrollLeft = rx * WORLD_W * zoom - v.clientWidth / 2;
      v.scrollTop = ry * WORLD_H * zoom - v.clientHeight / 2;
    }
  };

  // 미니맵의 viewport 표시 좌표
  const v = viewportRef.current;
  const mmScale = MINIMAP_W / (WORLD_W * zoom);
  const vpRect = v ? {
    x: v.scrollLeft * mmScale,
    y: v.scrollTop * (MINIMAP_H / (WORLD_H * zoom)),
    w: Math.min(MINIMAP_W, v.clientWidth * mmScale),
    h: Math.min(MINIMAP_H, v.clientHeight * (MINIMAP_H / (WORLD_H * zoom))),
  } : { x: 0, y: 0, w: 60, h: 40 };

  return (
    <div style={{ position: 'relative', width: '100%', height: 'calc(100vh - 112px)', background: 'var(--c-bg)', overflow: 'hidden' }}>
      {/* 스크롤 가능 뷰포트 */}
      <div ref={viewportRef} onWheel={onWheel} onMouseDown={onCanvasDown}
        style={{
          position: 'absolute', inset: 0, overflow: 'auto',
          cursor: spaceDown ? (panning ? 'grabbing' : 'grab') : 'default',
        }}>
        {/* 가상 캔버스 (zoom 적용된 크기) */}
        <div style={{
          position: 'relative',
          width: WORLD_W * zoom, height: WORLD_H * zoom,
          backgroundImage: `radial-gradient(${C.dim2} 1.2px, transparent 1.2px)`,
          backgroundSize: `${32 * zoom}px ${32 * zoom}px`,
          backgroundColor: 'transparent',
        }}>
          {/* 펜 캔버스 */}
          <canvas ref={canvasRef}
            style={{ position: 'absolute', inset: 0, width: WORLD_W * zoom, height: WORLD_H * zoom, pointerEvents: mode === 'pen' ? 'auto' : 'none' }} />

          {/* 영역 박스 */}
          <div style={{ position: 'absolute', inset: 0, transform: `scale(${zoom})`, transformOrigin: '0 0', pointerEvents: 'none', zIndex: 2, width: WORLD_W, height: WORLD_H }}>
            {areas.map(a => (
              <div key={a.id}
                onMouseDown={(e) => onAreaDown(e, a.id)}
                onDoubleClick={(e) => { if (mode !== 'pen' && !spaceDown) { e.stopPropagation(); setAreaEdit(a); } }}
                onMouseEnter={() => setHoverArea(a.id)}
                onMouseLeave={() => setHoverArea(null)}
                title="더블클릭으로 편집 · ↘ 핸들로 크기 조정"
                style={{
                  position: 'absolute', left: a.x, top: a.y, width: a.w, height: a.h,
                  border: `1.5px dashed ${a.color}88`, borderRadius: 10, background: `${a.color}08`,
                  pointerEvents: mode === 'pen' ? 'none' : 'auto',
                  cursor: dragArea === a.id ? 'grabbing' : (spaceDown ? 'grab' : 'grab'), zIndex: 10,
                }}>
                <div style={{
                  position: 'absolute', top: -11, left: 12, padding: '3px 12px',
                  background: C.bg, color: a.color, fontSize: 11, fontWeight: 800, letterSpacing: 1.2,
                  border: `1px solid ${a.color}66`, borderRadius: 6, pointerEvents: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                }}>{a.name}</div>
                <div onMouseDown={(e) => onAreaResizeDown(e, a.id)}
                  style={{
                    position: 'absolute', right: 0, bottom: 0, width: 28, height: 28, cursor: 'nwse-resize',
                    background: a.color, borderTopLeftRadius: 4,
                    opacity: hoverArea === a.id ? 1 : 0.75, zIndex: 50,
                    clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                    pointerEvents: 'auto',
                  }} title="크기 조정" />
              </div>
            ))}
          </div>

          {/* 카드 + 날짜 */}
          <div style={{ position: 'absolute', inset: 0, transform: `scale(${zoom})`, transformOrigin: '0 0', pointerEvents: 'none', zIndex: 3, width: WORLD_W, height: WORLD_H }}>
            {cards.map(card => (
              <div key={card.id}
                onMouseDown={(e) => onCardDown(e, card.id)}
                onDoubleClick={(e) => { if (mode !== 'pen' && !spaceDown) { e.stopPropagation(); setCardEdit(card); } }}
                title="더블클릭으로 편집 · ↘ 핸들로 크기 조정"
                style={{
                  position: 'absolute', left: card.x, top: card.y, width: card.w, minHeight: card.h,
                  padding: '12px 14px', background: C.surface,
                  border: `1px solid ${card.color}33`, borderTop: `3px solid ${card.color}`,
                  borderRadius: 8, cursor: dragCard === card.id ? 'grabbing' : 'grab', userSelect: 'none',
                  boxShadow: dragCard === card.id ? `0 12px 40px rgba(0,0,0,0.7)` : '0 2px 16px rgba(0,0,0,0.5)',
                  zIndex: dragCard === card.id ? 100 : 20,
                  pointerEvents: mode === 'pen' ? 'none' : 'auto',
                }}>
                <div style={{ fontSize: 10, color: card.color, marginBottom: 5, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase' }}>{TYPE_LABEL[card.type]}</div>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 700, marginBottom: 6 }}>{card.title}</div>
                <div style={{ fontSize: 12, color: C.dim, whiteSpace: 'pre-line', lineHeight: 1.6 }}>{card.content}</div>
                {card.type === 'file' && (
                  <div style={{ marginTop: 8 }}>
                    {!card.file ? (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <label onMouseDown={(e) => e.stopPropagation()}
                          style={{ flex: 1, minWidth: 0, padding: '7px 10px', background: C.green, color: '#000', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', textAlign: 'center' }}>
                          💻 PC에서
                          <input type="file" style={{ display: 'none' }} onChange={(e) => onFilePick(card.id, e.target.files?.[0])} />
                        </label>
                        <button onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); setFilePickerCard(card.id); }}
                          style={{ flex: 1, minWidth: 0, padding: '7px 10px', background: C.blue, color: '#fff', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: 'none' }}>
                          📁 파일탭에서
                        </button>
                      </div>
                    ) : (
                      <div>
                        {card.preview && card.file?.ext === 'pdf' && (
                          <div onMouseDown={(e) => e.stopPropagation()} style={{ marginBottom: 8 }}>
                            <PdfThumbnail blob={card.file?.blob} url={card.file?.url}
                              width={card.w - 28} height={Math.max(180, card.h - 130)} />
                          </div>
                        )}
                        {card.preview && card.file?.ext === 'pptx' && (
                          <div onMouseDown={(e) => e.stopPropagation()} style={{
                            marginBottom: 8, padding: 12, background: '#fff', borderRadius: 4,
                            minHeight: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                            color: '#333', textAlign: 'center', border: `1px solid ${C.border}`,
                          }}>
                            <div style={{ fontSize: 36 }}>📊</div>
                            <div style={{ fontSize: 10, fontWeight: 700, wordBreak: 'break-all' }}>{card.file.name}</div>
                            <div style={{ fontSize: 9, color: '#888' }}>SharePoint URL 필요 (Office Online)</div>
                          </div>
                        )}
                        {card.preview && !['pdf', 'pptx'].includes(card.file?.ext) && (
                          <div style={{ marginBottom: 8, padding: 10, background: C.surface2, borderRadius: 4, fontSize: 10, color: C.dim, textAlign: 'center' }}>
                            ⚠ {(card.file?.ext || '').toUpperCase()} 형식은 미리보기 미지원
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                          <button onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); togglePreview(card.id); }}
                            title="미리보기 토글"
                            style={{ flex: 1, minWidth: 80, padding: '5px 8px', borderRadius: 4, border: `1px solid ${card.preview ? C.gold : C.border}`, background: card.preview ? C.goldBg : C.surface2, color: card.preview ? C.gold : C.dim, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                            {card.preview ? '👁 미리보기 ON' : '👁 미리보기'}
                          </button>
                          <span style={{ fontSize: 9, padding: '5px 6px', background: C.surface3, color: C.dim, borderRadius: 4, fontWeight: 700, letterSpacing: 0.5 }}>
                            {card.file.source === 'tab' ? '📁 탭' : '💻 PC'}
                          </span>
                          <button onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation();
                              beforeChange();
                              setCards(prev => prev.map(c => c.id === card.id ? { ...c, file: null, preview: false, title: '새 파일', content: '↓ 클릭해 파일 첨부' } : c));
                            }}
                            title="파일 제거" style={{ padding: '5px 8px', borderRadius: 4, border: `1px solid ${C.red}44`, background: C.redBg, color: C.red, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>↻</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {card.type === 'media' && (
                  <div style={{ marginTop: 8 }}>
                    {!card.file ? (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <label onMouseDown={(e) => e.stopPropagation()}
                          style={{ flex: 1, minWidth: 0, padding: '7px 10px', background: card.color, color: '#000', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', textAlign: 'center' }}>
                          💻 PC에서
                          <input type="file" accept="video/*,audio/*,image/*" style={{ display: 'none' }} onChange={(e) => onFilePick(card.id, e.target.files?.[0])} />
                        </label>
                        <button onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); setFilePickerCard(card.id); }}
                          style={{ flex: 1, minWidth: 0, padding: '7px 10px', background: C.blue, color: '#fff', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: 'none' }}>
                          📁 파일탭에서
                        </button>
                      </div>
                    ) : (
                      <div>
                        {card.preview && (
                          <div onMouseDown={(e) => e.stopPropagation()} style={{ marginBottom: 8 }}>
                            <MediaPreview file={card.file} width={card.w - 28} height={Math.max(120, card.h - 130)} />
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                          <button onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); togglePreview(card.id); }}
                            style={{ flex: 1, minWidth: 80, padding: '5px 8px', borderRadius: 4, border: `1px solid ${card.preview ? C.gold : C.border}`, background: card.preview ? C.goldBg : C.surface2, color: card.preview ? C.gold : C.dim, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                            {card.preview ? '▶ 재생 ON' : '▶ 재생'}
                          </button>
                          <span style={{ fontSize: 9, padding: '5px 6px', background: C.surface3, color: C.dim, borderRadius: 4, fontWeight: 700, letterSpacing: 0.5 }}>
                            {card.file.source === 'tab' ? '📁 탭' : '💻 PC'}
                          </span>
                          <button onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation();
                              beforeChange();
                              setCards(prev => prev.map(c => c.id === card.id ? { ...c, file: null, preview: false, title: '새 미디어', content: '↓ 클릭해 파일 첨부' } : c));
                            }}
                            title="미디어 제거" style={{ padding: '5px 8px', borderRadius: 4, border: `1px solid ${C.red}44`, background: C.redBg, color: C.red, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>↻</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {Object.keys(card.reactions || {}).length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {Object.entries(card.reactions).map(([emo, n]) => (
                      <span key={emo} style={{ fontSize: 11, padding: '2px 6px', background: C.surface2, borderRadius: 10, color: C.dim }}>{emo} {n}</span>
                    ))}
                  </div>
                )}
                <div style={{ position: 'absolute', bottom: 6, right: 6, display: 'flex', gap: 2 }}>
                  <button onClick={(e) => { e.stopPropagation(); setEmojiCard(card); }} title="반응"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, padding: 3, color: C.dim }}>😊</button>
                  <button onClick={(e) => { e.stopPropagation(); setCommentCard(card); }} title="댓글"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, padding: 3, color: C.dim }}>
                    💬{card.comments?.length > 0 ? card.comments.length : ''}
                  </button>
                </div>
                <button
                  onMouseDown={(e) => { e.stopPropagation(); e.nativeEvent?.stopImmediatePropagation?.(); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); setCardEdit(card); }}
                  title="카드 편집 (또는 카드 더블클릭)"
                  style={{ position: 'absolute', top: 6, right: 30, background: card.color, border: 'none', color: '#000', cursor: 'pointer', fontSize: 11, padding: '3px 7px', borderRadius: 4, fontWeight: 800, opacity: 0.9, zIndex: 50 }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'scale(1)'; }}>✏ 편집</button>
                <button
                  onMouseDown={(e) => { e.stopPropagation(); e.nativeEvent?.stopImmediatePropagation?.(); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); deleteCard(card.id); }}
                  style={{ position: 'absolute', top: 8, right: 8, background: 'transparent', border: 'none', color: C.dim2, cursor: 'pointer', fontSize: 16, padding: '2px 6px', fontWeight: 700, zIndex: 50 }}>×</button>
                <div onMouseDown={(e) => onCardResizeDown(e, card.id)}
                  style={{ position: 'absolute', right: 0, bottom: 0, width: 18, height: 18, cursor: 'nwse-resize',
                    background: card.color, borderTopLeftRadius: 4, opacity: 0.5, zIndex: 50,
                    clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }} title="크기 조정" />
              </div>
            ))}

            {dates.map(d => {
              const dday = calcDDay(d.date);
              const isPast = dday < 0;
              const isUrgent = dday >= 0 && dday <= 3;
              const c = isPast ? C.dim : (isUrgent ? C.red : d.color);
              return (
                <div key={d.id} onMouseDown={(e) => onDateDown(e, d.id)}
                  onDoubleClick={(e) => { if (mode !== 'pen' && !spaceDown) { e.stopPropagation(); setDateEdit(d); } }}
                  style={{
                    position: 'absolute', left: d.x, top: d.y, width: d.w, height: d.h,
                    background: C.surface, border: `2px solid ${c}88`, borderRadius: 8,
                    cursor: dragDate === d.id ? 'grabbing' : 'grab', userSelect: 'none',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 12,
                    boxShadow: '0 2px 16px rgba(0,0,0,0.5)', zIndex: dragDate === d.id ? 100 : 25,
                    pointerEvents: mode === 'pen' ? 'none' : 'auto',
                  }}>
                  <div style={{ fontSize: 10, color: c, fontWeight: 800, letterSpacing: 1.5, marginBottom: 4 }}>📅 {d.label}</div>
                  <div style={{ fontSize: Math.min(d.w * 0.22, d.h * 0.45), color: c, fontWeight: 900, lineHeight: 1 }}>
                    {isPast ? `D+${Math.abs(dday)}` : (dday === 0 ? 'D-DAY' : `D-${dday}`)}
                  </div>
                  <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>{d.date}</div>
                  <button onClick={(e) => { e.stopPropagation(); deleteDate(d.id); }}
                    style={{ position: 'absolute', top: 6, right: 6, background: 'transparent', border: 'none', color: C.dim2, cursor: 'pointer', fontSize: 13 }}>×</button>
                  <div onMouseDown={(e) => onDateResizeDown(e, d.id)}
                    style={{ position: 'absolute', right: 2, bottom: 2, width: 18, height: 18, cursor: 'nwse-resize', background: c, borderRadius: 2, opacity: 0.7, clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 툴바 */}
      <div style={{ position: 'absolute', top: 20, left: 32, display: 'flex', gap: 8, zIndex: 30, flexWrap: 'wrap', alignItems: 'center', padding: '8px 10px', background: 'rgba(20,20,20,0.85)', borderRadius: 10, backdropFilter: 'blur(12px)', border: `1px solid ${C.border}` }}>
        {[['select', '🖱 선택'], ['pen', '✏ 펜']].map(([m, l]) => (
          <button key={m} onClick={() => setMode(m)}
            style={{ padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: `1px solid ${mode === m ? C.gold : C.border}`, background: mode === m ? C.goldBg : C.surface, color: mode === m ? C.gold : C.dim }}>{l}</button>
        ))}
        <button onClick={addArea} style={{ padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: `1px solid ${C.border}`, background: C.surface, color: C.dim }}>⬚ 영역</button>
        <div style={{ width: 1, height: 22, background: C.border, margin: '0 4px' }} />
        {[['+ 메모', 'note'], ['+ 수정', 'task'], ['+ 파일', 'file'], ['+ 미디어', 'media']].map(([l, t]) => (
          <button key={t} onClick={() => addCard(t)} style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.dim, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>{l}</button>
        ))}
        <button onClick={addDate} style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${C.red}44`, background: C.redBg, color: C.red, fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>📅 마감일</button>
        <div style={{ width: 1, height: 22, background: C.border, margin: '0 4px' }} />
        <button onClick={undo} disabled={!hist.canUndo} title="Ctrl+Z"
          style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${hist.canUndo ? C.gold : C.border}`, background: hist.canUndo ? C.goldBg : C.surface, color: hist.canUndo ? C.gold : C.dim2, fontSize: 12, cursor: hist.canUndo ? 'pointer' : 'not-allowed', fontWeight: 700 }}>
          ↶ 되돌리기 {hist.past.length > 0 && `(${hist.past.length})`}
        </button>
        <button onClick={redo} disabled={!hist.canRedo} title="Ctrl+Y / Ctrl+Shift+Z"
          style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${hist.canRedo ? C.blue : C.border}`, background: hist.canRedo ? C.blueBg : C.surface, color: hist.canRedo ? C.blue : C.dim2, fontSize: 12, cursor: hist.canRedo ? 'pointer' : 'not-allowed', fontWeight: 700 }}>
          ↷ 다시 실행 {hist.future.length > 0 && `(${hist.future.length})`}
        </button>

        {mode === 'pen' && (
          <>
            <div style={{ width: 1, height: 22, background: C.border, margin: '0 4px' }} />
            <div style={{ display: 'flex', gap: 4 }}>
              {[C.gold, C.blue, C.green, C.red, C.purple, C.text].map(col => (
                <button key={col} onClick={() => setPenColor(col)}
                  style={{ width: 22, height: 22, borderRadius: '50%', cursor: 'pointer', background: col, border: penColor === col ? `2px solid ${C.text}` : `1px solid ${C.border}` }} />
              ))}
            </div>
            <input type="range" min="1" max="10" value={penWidth} onChange={e => setPenWidth(+e.target.value)} style={{ width: 80 }} />
            <button onClick={() => { beforeChange(); setPaths([]); addLog('윤대표', 'whiteboard', '펜 전체 지우기'); }}
              style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.red}44`, background: C.redBg, color: C.red, fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>전체 지우기</button>
          </>
        )}

        <div style={{ width: 1, height: 22, background: C.border, margin: '0 4px' }} />
        <button onClick={() => setZoom(z => Math.min(z + 0.1, 2))} title="확대 (Ctrl+휠)"
          style={{ width: 32, height: 32, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.text, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>+</button>
        <button onClick={() => setZoom(0.8)} title="100% (기본)"
          style={{ minWidth: 56, height: 32, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>{Math.round(zoom * 100)}%</button>
        <button onClick={() => setZoom(z => Math.max(z - 0.1, 0.2))} title="축소 (Ctrl+휠)"
          style={{ width: 32, height: 32, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.text, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>−</button>
      </div>

      {/* 하단 안내 */}
      <div style={{ position: 'absolute', bottom: 20, left: 32, fontSize: 11, color: C.dim, zIndex: 20, padding: '6px 12px', background: 'rgba(20,20,20,0.8)', borderRadius: 6, backdropFilter: 'blur(8px)' }}>
        카드 {cards.length} · 영역 {areas.length} · 마감일 {dates.length} · 스트로크 {paths.length} · 더블클릭=편집 · Space+드래그=패닝 · Ctrl+휠=줌 · Ctrl+Z/Y
      </div>

      {/* ── 미니맵 (우하단) ── */}
      <div style={{
        position: 'absolute', bottom: 20, right: 20, width: MINIMAP_W, height: MINIMAP_H + 22,
        background: 'rgba(20,20,20,0.85)', border: `1px solid ${C.border}`, borderRadius: 8,
        backdropFilter: 'blur(8px)', zIndex: 40, overflow: 'hidden',
      }}>
        <div style={{ padding: '4px 8px', fontSize: 10, color: C.dim, fontWeight: 700, letterSpacing: 0.5, borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between' }}>
          <span>🗺 미니맵</span>
          <span>{WORLD_W}×{WORLD_H}</span>
        </div>
        <svg width={MINIMAP_W} height={MINIMAP_H} onClick={onMinimapClick}
          style={{ cursor: 'pointer', display: 'block', background: 'var(--c-bg)' }}>
          {/* 영역 박스 */}
          {areas.map(a => (
            <rect key={a.id}
              x={a.x * (MINIMAP_W / WORLD_W)}
              y={a.y * (MINIMAP_H / WORLD_H)}
              width={a.w * (MINIMAP_W / WORLD_W)}
              height={a.h * (MINIMAP_H / WORLD_H)}
              fill={`${a.color}22`} stroke={a.color} strokeWidth="0.5" />
          ))}
          {/* 카드 */}
          {cards.map(c => (
            <rect key={c.id}
              x={c.x * (MINIMAP_W / WORLD_W)}
              y={c.y * (MINIMAP_H / WORLD_H)}
              width={Math.max(2, c.w * (MINIMAP_W / WORLD_W))}
              height={Math.max(2, c.h * (MINIMAP_H / WORLD_H))}
              fill={c.color} opacity="0.8" />
          ))}
          {/* 날짜 */}
          {dates.map(d => (
            <rect key={d.id}
              x={d.x * (MINIMAP_W / WORLD_W)}
              y={d.y * (MINIMAP_H / WORLD_H)}
              width={Math.max(3, d.w * (MINIMAP_W / WORLD_W))}
              height={Math.max(3, d.h * (MINIMAP_H / WORLD_H))}
              fill={d.color} opacity="0.9" />
          ))}
          {/* 현재 viewport */}
          <rect x={vpRect.x} y={vpRect.y} width={vpRect.w} height={vpRect.h}
            fill="none" stroke={C.gold} strokeWidth="1.5" />
        </svg>
      </div>

      {/* 팝업들 */}
      {emojiCard && createPortal(
        <div onClick={() => setEmojiCard(null)} onMouseDown={e => e.stopPropagation()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }}>
          <div onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: 12, padding: 20, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 12, color: C.dim, marginBottom: 12, fontWeight: 700 }}>반응 선택 · {emojiCard.title}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
              {EMOJI_LIST.map(em => (
                <button key={em} onClick={() => addReaction(emojiCard.id, em)}
                  style={{ width: 44, height: 44, fontSize: 22, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer' }}>{em}</button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
      {commentCard && (
        <CommentPopup card={cards.find(c => c.id === commentCard.id) || commentCard}
          onClose={() => setCommentCard(null)} onAdd={(text) => addComment(commentCard.id, text)} />
      )}
      {areaEdit && (
        <AreaEditPopup area={areaEdit}
          onSave={(updated) => { beforeChange(); setAreas(prev => prev.map(a => a.id === updated.id ? updated : a)); setAreaEdit(null); addLog('윤대표', 'whiteboard', `영역 편집`); }}
          onDelete={() => deleteArea(areaEdit.id)} onClose={() => setAreaEdit(null)} />
      )}
      {dateEdit && (
        <DateEditPopup d={dateEdit}
          onSave={(updated) => { beforeChange(); setDates(prev => prev.map(x => x.id === updated.id ? updated : x)); setDateEdit(null); addLog('윤대표', 'whiteboard', `마감일 편집`); }}
          onDelete={() => deleteDate(dateEdit.id)} onClose={() => setDateEdit(null)} />
      )}
      {cardEdit && (
        <CardEditPopup card={cardEdit}
          onSave={(updated) => { beforeChange(); setCards(prev => prev.map(c => c.id === updated.id ? updated : c)); setCardEdit(null); addLog('윤대표', 'whiteboard', `카드 편집: ${updated.title}`); }}
          onDelete={() => { beforeChange(); setCards(prev => prev.filter(c => c.id !== cardEdit.id)); setCardEdit(null); addLog('윤대표', 'whiteboard', `카드 삭제: ${cardEdit.title}`); }}
          onClose={() => setCardEdit(null)} />
      )}
      {filePickerCard && (
        <FilePickerModal
          onClose={() => setFilePickerCard(null)}
          onPick={(f) => {
            beforeChange();
            setCards(prev => prev.map(c => c.id === filePickerCard ? {
              ...c, title: f.name, content: `${f.size} · ${f.date} · 👤 ${f.uploader}`,
              file: { name: f.name, size: f.size, ext: f.type, source: 'tab', tabId: f.id },
              preview: c.type === 'media',
            } : c));
            addLog('윤대표', 'file', `파일탭 연결: ${f.name}`);
          }} />
      )}
    </div>
  );
}

function CommentPopup({ card, onClose, onAdd }) {
  const [text, setText] = useState('');
  return createPortal(
    <div onClick={onClose} onMouseDown={e => e.stopPropagation()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: 12, padding: 24, border: `1px solid ${C.border}`, width: 440 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 14, color: C.text, fontWeight: 800 }}>💬 {card.title}</div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: C.dim, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 14 }}>
          {(card.comments || []).length === 0 && <div style={{ fontSize: 12, color: C.dim, padding: '20px 0', textAlign: 'center' }}>댓글이 없습니다</div>}
          {(card.comments || []).map(cm => (
            <div key={cm.id} style={{ padding: '10px 12px', background: C.surface2, borderRadius: 8, marginBottom: 6 }}>
              <div style={{ fontSize: 11, color: USERS[cm.by] || C.gold, fontWeight: 700, marginBottom: 4 }}>{cm.by}</div>
              <div style={{ fontSize: 13, color: C.text }}>{cm.text}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input value={text} onChange={e => setText(e.target.value)} placeholder="댓글 입력..."
            onKeyDown={e => { if (e.key === 'Enter') { onAdd(text); setText(''); } }}
            style={{ flex: 1, padding: '10px 12px', background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 13, outline: 'none' }} />
          <button onClick={() => { onAdd(text); setText(''); }} style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: C.gold, color: '#000', fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>등록</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AreaEditPopup({ area, onSave, onDelete, onClose }) {
  const [name, setName] = useState(area.name);
  const [color, setColor] = useState(area.color);
  const [w, setW] = useState(area.w);
  const [h, setH] = useState(area.h);
  return createPortal(
    <div onClick={onClose} onMouseDown={e => e.stopPropagation()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: 12, padding: 24, border: `1px solid ${C.border}`, width: 380 }}>
        <div style={{ fontSize: 14, color: C.text, fontWeight: 800, marginBottom: 16 }}>⬚ 영역 편집</div>
        <label style={{ fontSize: 11, color: C.dim, fontWeight: 700 }}>이름</label>
        <input value={name} onChange={e => setName(e.target.value)} autoFocus
          style={{ width: '100%', padding: '8px 10px', marginTop: 4, marginBottom: 12, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 13, outline: 'none' }} />
        <label style={{ fontSize: 11, color: C.dim, fontWeight: 700 }}>색상</label>
        <div style={{ display: 'flex', gap: 6, marginTop: 6, marginBottom: 12 }}>
          {PALETTE.map(col => (
            <button key={col} onClick={() => setColor(col)}
              style={{ width: 28, height: 28, borderRadius: '50%', background: col, border: color === col ? `2px solid ${C.text}` : `1px solid ${C.border}`, cursor: 'pointer' }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, color: C.dim, fontWeight: 700 }}>너비</label>
            <input type="number" value={w} onChange={e => setW(+e.target.value)}
              style={{ width: '100%', padding: '8px 10px', marginTop: 4, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 13, outline: 'none' }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, color: C.dim, fontWeight: 700 }}>높이</label>
            <input type="number" value={h} onChange={e => setH(+e.target.value)}
              style={{ width: '100%', padding: '8px 10px', marginTop: 4, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 13, outline: 'none' }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'space-between' }}>
          <button onClick={onDelete} style={{ padding: '7px 14px', borderRadius: 6, border: `1px solid ${C.red}44`, background: C.redBg, color: C.red, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>삭제</button>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={onClose} style={{ padding: '7px 14px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.dim, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>취소</button>
            <button onClick={() => onSave({ ...area, name, color, w, h })} style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: C.gold, color: '#000', fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>저장</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function DateEditPopup({ d, onSave, onDelete, onClose }) {
  const [label, setLabel] = useState(d.label);
  const [date, setDate] = useState(d.date);
  const [color, setColor] = useState(d.color);
  const [w, setW] = useState(d.w);
  const [h, setH] = useState(d.h);
  return createPortal(
    <div onClick={onClose} onMouseDown={e => e.stopPropagation()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: 12, padding: 24, border: `1px solid ${C.border}`, width: 380 }}>
        <div style={{ fontSize: 14, color: C.text, fontWeight: 800, marginBottom: 16 }}>📅 마감일 편집</div>
        <label style={{ fontSize: 11, color: C.dim, fontWeight: 700 }}>라벨</label>
        <input value={label} onChange={e => setLabel(e.target.value)} autoFocus
          style={{ width: '100%', padding: '8px 10px', marginTop: 4, marginBottom: 12, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 13, outline: 'none' }} />
        <label style={{ fontSize: 11, color: C.dim, fontWeight: 700 }}>날짜</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          style={{ width: '100%', padding: '8px 10px', marginTop: 4, marginBottom: 12, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 13, outline: 'none', colorScheme: 'dark' }} />
        <label style={{ fontSize: 11, color: C.dim, fontWeight: 700 }}>색상</label>
        <div style={{ display: 'flex', gap: 6, marginTop: 6, marginBottom: 12 }}>
          {PALETTE.map(col => (
            <button key={col} onClick={() => setColor(col)}
              style={{ width: 28, height: 28, borderRadius: '50%', background: col, border: color === col ? `2px solid ${C.text}` : `1px solid ${C.border}`, cursor: 'pointer' }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, color: C.dim, fontWeight: 700 }}>너비</label>
            <input type="number" value={w} onChange={e => setW(+e.target.value)}
              style={{ width: '100%', padding: '8px 10px', marginTop: 4, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 13, outline: 'none' }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, color: C.dim, fontWeight: 700 }}>높이</label>
            <input type="number" value={h} onChange={e => setH(+e.target.value)}
              style={{ width: '100%', padding: '8px 10px', marginTop: 4, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 13, outline: 'none' }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'space-between' }}>
          <button onClick={onDelete} style={{ padding: '7px 14px', borderRadius: 6, border: `1px solid ${C.red}44`, background: C.redBg, color: C.red, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>삭제</button>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={onClose} style={{ padding: '7px 14px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.dim, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>취소</button>
            <button onClick={() => onSave({ ...d, label, date, color, w, h })} style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: C.gold, color: '#000', fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>저장</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// blob → object URL useMemo + cleanup
function useBlobUrl(blob) {
  const url = useMemo(() => blob ? URL.createObjectURL(blob) : null, [blob]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  return url;
}

function MediaPreview({ file, width, height }) {
  const url = useBlobUrl(file?.blob);
  const ext = (file?.ext || '').toLowerCase();
  const VIDEO = ['mp4', 'webm', 'mov', 'm4v', 'ogv'];
  const AUDIO = ['mp3', 'wav', 'm4a', 'ogg', 'flac'];
  const IMAGE = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];

  const src = url || file?.url;
  if (!src) {
    return (
      <div style={{ width, height, background: C.surface2, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: C.dim, textAlign: 'center', padding: 12 }}>
        ⚠ 미리보기 URL 없음<br/>(파일탭은 SharePoint URL 필요)
      </div>
    );
  }

  if (VIDEO.includes(ext)) {
    return <video src={src} controls style={{ width, maxHeight: height, borderRadius: 4, background: '#000', display: 'block' }} />;
  }
  if (AUDIO.includes(ext)) {
    return (
      <div style={{ width, padding: 12, background: C.surface2, borderRadius: 4, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
        <div style={{ fontSize: 24 }}>🎵</div>
        <audio src={src} controls style={{ width: '100%' }} />
      </div>
    );
  }
  if (IMAGE.includes(ext)) {
    return <img src={src} alt={file?.name} style={{ width, maxHeight: height, objectFit: 'contain', borderRadius: 4, background: C.surface2, display: 'block' }} />;
  }
  return (
    <div style={{ width, height, background: C.surface2, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: C.dim, textAlign: 'center', padding: 12 }}>
      ⚠ {ext.toUpperCase()} 미지원
    </div>
  );
}
